import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import ResumeBox from "@/common/components/ResumeBox";
import ResumeItem from "@/common/components/ResumeItem";
import TextArea from "@/common/components/TextArea";
import { UnitTextField } from "@/common/components/UnitTextField";
import { formatCurrency } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { DiagnosticPieceData } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/diagnosticEntry/diagnosticEntry.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { FileEditIcon, WrenchIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useUpdateDiagnosticObjectivePiece } from "../../../../../../hooks/diagnosticObjectivePieces/useUpdateDiagnosticObjectivePiece";
import { diagnosticObjectivePieceSchema, DiagnosticObjectivePieceValidationSchema } from "../../../../../../schemas/diagnosticObjectivePiece.schema";

interface UpdateDiagnosticObjectivePieceProps {
  diagnosticPiece: DiagnosticPieceData;
}

const UpdateDiagnosticObjectivePiece = ({
  diagnosticPiece,
}: UpdateDiagnosticObjectivePieceProps) => {
  const { forwardingId } = useParams();

  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [formDisabled, setFormDisabled] = useState(false);

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: {
      errors,
    },
  } = useForm<DiagnosticObjectivePieceValidationSchema>({
    resolver: zodResolver(diagnosticObjectivePieceSchema),
    disabled: formDisabled,
  });

  const {
    unit_price: watchUnitPrice,
    quantity: watchQuantity,
  } = useWatch({
    control,
  });

  const {
    mutate: updateDiagnosticObjectivePiece,
    isPending: updateDiagnosticObjectivePieceIsPending,
  } = useUpdateDiagnosticObjectivePiece({
    diagnosticObjectivePiece: diagnosticPiece,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] });

        setIsOpen(false);
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      }
    },
  });

  const onSubmit: SubmitHandler<DiagnosticObjectivePieceValidationSchema> = (data) => updateDiagnosticObjectivePiece(data);

  const totalValue = useMemo(() => {
    const parsedUnitPrice = watchUnitPrice ? unFormatCurrency(watchUnitPrice) : 0;
    const parsedQuantity = watchQuantity ? unFormatUnknownUnit(watchQuantity) : 0;

    return (parsedUnitPrice * parsedQuantity) || 0;
  }, [watchUnitPrice, watchQuantity]);

  useEffect(() => {
    setTimeout(() => {
      reset({
        description: diagnosticPiece?.description || '',
        unit_price: formatCurrency(parseFloat(diagnosticPiece?.unit_price)),
        quantity: diagnosticPiece?.quantity?.toString(),
        warranty_in_days: `${diagnosticPiece?.warranty_in_days || 0} Dias`,
      });
    }, !isOpen ? 500 : 0);
  }, [isOpen]);

  useEffect(() => {
    setFormDisabled(updateDiagnosticObjectivePieceIsPending);
  }, [updateDiagnosticObjectivePieceIsPending]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            variant="link-white"
            operationKlassToCheckPermission="Establishment::Maintenance::DiagnosticObjectivePieces::Update"
          >
            <FileEditIcon strokeWidth={1} />
          </Button>
        )}
        title="Editar Peça"
        titleClass="text-2xl"
        bodyClassName="text-white-800 overflow-y-auto max-h-[calc(100vh-200px)]"
        useCancellableFooter
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonText="Salvar"
        cancelButtonClassName="bg-white-200"
        confirmButtonClassName="bg-primary-blue"
        confirmButtonIsLoading={updateDiagnosticObjectivePieceIsPending}
        body={
          <div className="flex flex-col gap-4">
            <Box className="bg-white-200 p-4 flex gap-4">
              <ImageCarousel
                files={diagnosticPiece?.piece?.images?.map((image) => ({ id: image?.url, file: image?.url })) || []}
                Placeholder={(
                  <ImageWithPlaceholder
                    className="size-20 rounded-md"
                    iconClassName="size-10"
                    placeholderIcon={WrenchIcon}
                  />
                )}
                className="size-20"
              />

              <div className="flex flex-col gap-2">
                <p className="text-base font-bold">
                  {diagnosticPiece?.piece?.display_name || '--'}
                </p>

                <div className="flex gap-3">
                  <ResumeItem
                    className="text-xs"
                    label="Código da peça"
                    value={diagnosticPiece?.piece?.number || '--'}
                  />

                  <ResumeItem
                    className="text-xs"
                    label="Fabricante"
                    value={diagnosticPiece?.piece?.brand?.display_name || '--'}
                  />
                </div>
              </div>
            </Box>

            <div className="flex flex-col gap-4">
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <TextArea
                    label="Razão pela troca de peças"
                    rows={4}
                    {...field}
                    errorMessage={errors.description?.message}
                  />
                )}
              />

              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Controller
                    control={control}
                    name="unit_price"
                    render={({ field }) => (
                      <CurrencyTextField
                        label="Preço unitário"
                        markAsRequired
                        {...field}
                        errorMessage={errors.unit_price?.message}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="quantity"
                    render={({ field }) => (
                      <UnitTextField
                        label="Quantidade"
                        markAsRequired
                        decimals={0}
                        {...field}
                        errorMessage={errors.quantity?.message}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="warranty_in_days"
                    render={({ field }) => (
                      <UnitTextField
                        label="Garantia"
                        markAsRequired
                        unit="Dias"
                        decimals={0}
                        {...field}
                        errorMessage={errors.warranty_in_days?.message}
                      />
                    )}
                  />
                </div>

                <ResumeBox
                  label="Valor total"
                  value={formatCurrency(totalValue)}
                  padding="small"
                  boxClassName="bg-white-200 rounded-md"
                />
              </div>
            </div>
          </div>
        }
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default UpdateDiagnosticObjectivePiece;
